import { createColumnHelper, PaginationState } from '@tanstack/table-core';
import clsx from 'clsx';
import dedent from 'dedent';
import React, {
  FormEvent,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { FiEdit2, FiPlus, FiTrash2 } from 'react-icons/fi';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams
} from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import APIGateway from '@/api';
import { Button } from '@/components/button/button';
import { Icon } from '@/components/icon/icon';
import { Input } from '@/components/input/input';
import { Loader } from '@/components/loader/loader';
import Modal from '@/components/modal/modal';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { useNativeTable } from '@/components/nativeTable/useNativeTable';
import {
  buildColumnClassString,
  ColumnModEnums
} from '@/components/table/table';
import { Heading } from '@/components/typography/heading';
import {
  Prompt,
  PromptDeleteArgs,
  PromptsGetArgs,
  PromptsGetRes,
  PromptTypeEnum
} from '@/types/prompts';
import { parseParams } from '@/utils/searchParams';
import {
  OutputFormatEnum,
  TimeFormatter,
  TimeSourceTypeEnum
} from '@/utils/timeFormatter';

import promptStyles from './page.module.scss';

const columnHelper = createColumnHelper<Prompt>();
import { envVarsProvider } from '@/utils/envVars/envVars';

const DOCS_BASE_URL = envVarsProvider.get( 'DOCS_BASE_URL' );

type SearchParams = {
  n?: string;
  ta?: string;
  page?: string;
};

type ModalCreateProps = {
  newPromptType: PromptTypeEnum;
  onPromptTypeChange: ( type: PromptTypeEnum ) => void;
  onPromptCreate: ( e: FormEvent<HTMLFormElement> ) => void;
  onClose: () => void;
};

const ModalCreate = memo<ModalCreateProps>(
  ( { newPromptType, onPromptTypeChange, onPromptCreate, onClose } ) => (
    <Modal>
      <form onSubmit={ onPromptCreate } name="createForm">
        <Heading as="h2" size="md">
          Create New Prompt
        </Heading>
        <div className={ clsx( 'layout__col', 'pad' ) }>
          <label className="auth_flow_label" htmlFor="name">
            Name
          </label>
          <input
            className="auth_flow_input"
            id="name"
            name="name"
            type="text"
            required
          />
        </div>
        <div className={ clsx( 'layout__col', 'pad' ) }>
          <label className="auth_flow_label" htmlFor="description">
            Description
          </label>
          <input
            className="auth_flow_input"
            id="description"
            name="description"
            type="text"
          />
        </div>
        <div className={ clsx( 'layout__row', 'pad' ) }>
          <span className="auth_flow_label">Prompt Type</span>
          <div className="layout__row">
            <span
              className={ clsx(
                promptStyles.create_prompt_type,
                newPromptType === PromptTypeEnum.SIMPLE &&
                  promptStyles.selected
              ) }
              key="simple"
              onClick={ () => onPromptTypeChange( PromptTypeEnum.SIMPLE ) }
            >
              {PromptTypeEnum.SIMPLE.toUpperCase()}
            </span>
            |
            <span
              className={ clsx(
                promptStyles.create_prompt_type,
                newPromptType === PromptTypeEnum.MESSAGE &&
                  promptStyles.selected
              ) }
              key="message"
              onClick={ () => onPromptTypeChange( PromptTypeEnum.MESSAGE ) }
            >
              {PromptTypeEnum.MESSAGE.toUpperCase()}
            </span>
          </div>
        </div>
        <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
          <Button variant="secondary" size="sm" onClick={ onClose }>
            Cancel
          </Button>
          <Button variant="primary" size="sm" type="submit">
            Create
          </Button>
        </div>
      </form>
    </Modal>
  )
);

ModalCreate.displayName = 'ModalCreate';

type PromptsTableProps = PromptsGetArgs & {
  page: number;
  onPageChange: ( newPage: number ) => void;
};

const PromptsTable = memo<PromptsTableProps>(
  ( { name, tags, page, onPageChange } ) => {
    const [ isLoading, setIsLoading ] = useState<boolean>( true );
    const [ prompts, setPrompts ] = useState<Prompt[]>( [] );
    const [ totalPages, setTotalPages ] = useState<number>( 0 );
    const [ totalRows, setTotalRows ] = useState<number>( 0 );

    const fetchPrompts = useCallback( async() => {
      setIsLoading( true );

      try {
        const args: PromptsGetArgs = {
          name,
          tags,
          page
        };

        const promptsRes: PromptsGetRes =
          await APIGateway.prompts.promptsGet( args );

        setPrompts( promptsRes.prompts );
        setTotalPages( promptsRes.number_of_pages || 1 );
        // Estimate total rows from number of pages * page size
        setTotalRows( ( promptsRes.number_of_pages || 1 ) * 50 );
      } catch( e ) {
        console.error( 'Failed to fetch prompts:', e );
        toast( 'failed to get prompts' );
        setPrompts( [] );
      } finally {
        setIsLoading( false );
      }
    }, [ name, tags, page ] );

    const onDelete = useCallback(
      async( deleteReq: PromptDeleteArgs ) => {
        setIsLoading( true );

        try {
          await APIGateway.prompts.promptDelete( deleteReq );
          await fetchPrompts();
        } catch( e ) {
          console.error( 'Failed to delete prompts:', e );
          toast( 'failed to delete prompt' );
        } finally {
          setIsLoading( false );
        }
      },
      [ fetchPrompts ]
    );

    useEffect( () => {
      fetchPrompts().then();
    }, [ fetchPrompts ] );

    const columns = useMemo(
      () => [
        columnHelper.accessor( 'id.name', {
          header: 'Name',
          size: 250,
          cell: ( info ) => info.getValue(),
          meta: {
            classStr: buildColumnClassString( {
              width: 25,
              styles: ColumnModEnums.NO_OVERFLOW
            } )
          }
        } ),
        columnHelper.accessor( 'description', {
          header: 'Description',
          size: 180,
          cell: ( info ) => info.getValue(),
          meta: {
            classStr: buildColumnClassString( {
              width: 15,
              styles: ColumnModEnums.NO_OVERFLOW
            } )
          }
        } ),
        columnHelper.accessor( 'id.tags', {
          header: 'Tags',
          size: 150,
          cell: ( info ) => info.getValue()?.join( ',' ) ?? '',
          meta: {
            classStr: buildColumnClassString( {
              width: 15,
              styles: ColumnModEnums.NO_OVERFLOW
            } )
          }
        } ),
        columnHelper.display( {
          id: 'type',
          header: 'Type',
          size: 80,
          cell: ( info ) => {
            const prompt = info.row.original;
            return !!prompt.prompt_text
              ? PromptTypeEnum.SIMPLE
              : !!prompt.messages && prompt.messages.length
                  ? PromptTypeEnum.MESSAGE
                  : 'unknown';
          },
          meta: {
            classStr: buildColumnClassString( { width: 5 } )
          }
        } ),
        columnHelper.accessor( 'id.version', {
          header: 'Version',
          size: 100,
          cell: ( info ) => info.getValue(),
          meta: {
            classStr: buildColumnClassString( {
              width: 10,
              styles: ColumnModEnums.CENTER
            } )
          }
        } ),
        columnHelper.accessor( 'created_date', {
          header: 'Created',
          size: 120,
          cell: ( info ) => TimeFormatter( {
            sourceType: TimeSourceTypeEnum.ISO,
            value: info.getValue()!.toString(),
            outputFormat: OutputFormatEnum.OBJECT
          } ).date,
          meta: {
            classStr: buildColumnClassString( {
              width: 10,
              styles: ColumnModEnums.NO_OVERFLOW
            } )
          }
        } ),
        columnHelper.accessor( 'author', {
          header: 'Author',
          size: 200,
          cell: ( info ) => info.getValue(),
          meta: {
            classStr: buildColumnClassString( {
              width: 20,
              styles: ColumnModEnums.NO_OVERFLOW
            } )
          }
        } ),
        columnHelper.display( {
          id: 'actions',
          header: 'Actions',
          size: 100,
          cell: ( info ) => {
            const prompt = info.row.original;
            const editLinkParams: Record<string, string> = {
              n: prompt.id.name
            };
            if ( prompt.id.version !== null )
              editLinkParams.v = prompt.id.version.toString();
            if ( Array.isArray( prompt.id.tags ) )
              editLinkParams.ta = prompt.id.tags.join( ',' );

            const queryString = new URLSearchParams( editLinkParams ).toString();
            return (
              <div className="table_actions">
                <Link
                  className="table_actions_item"
                  to={ `/prompts/create?${ queryString }` }
                  key={ info.row.id + '-edit' }
                >
                  <FiEdit2 />
                </Link>
                <FiTrash2
                  className="table_actions_item"
                  key={ info.row.id + '-delete' }
                  onClick={ () => onDelete( {
                    name: prompt.id.name,
                    tags: prompt.id.tags?.join( ',' ) ?? null,
                    version: prompt.id.version
                  } ) }
                />
              </div>
            );
          },
          meta: {
            classStr: buildColumnClassString( {
              width: 10,
              styles: ColumnModEnums.CENTER
            } )
          }
        } )
      ],
      [ onDelete ]
    );

    const pagination: PaginationState = useMemo(
      () => ( {
        pageIndex: page,
        pageSize: 50 // This matches the backend default
      } ),
      [ page ]
    );

    const table = useNativeTable<Prompt>( {
      data: prompts,
      columns,
      enableSorting: false,
      manualPagination: true,
      pageCount: totalPages,
      rowCount: totalRows,
      state: { pagination },
      onPaginationChange: ( updater ) => {
        const newPagination =
          typeof updater === 'function' ? updater( pagination ) : updater;
        onPageChange( newPagination.pageIndex );
      }
    } );

    return isLoading ? (
      <Loader />
    ) : (
      <NativeTable table={ table } pagination="server" />
    );
  }
);

PromptsTable.displayName = 'PromptsTable';

function PromptsPage() {
  const [ searchParams ] = useSearchParams();
  const { pathname } = useLocation();
  const {
    n: qPromptName,
    ta: qTag,
    page
  } = useMemo( () => parseParams<SearchParams>( searchParams ), [ searchParams ] );
  const navigate = useNavigate();

  const handlePageChange = useCallback(
    ( newPage: number ) => {
      const params = new URLSearchParams( searchParams.toString() );
      params.set( 'page', newPage.toString() );
      navigate( `${ pathname }?${ params.toString() }` );
    },
    [ searchParams, pathname, navigate ]
  );
  const [ newPromptType, setNewPromptType ] = useState<PromptTypeEnum>(
    PromptTypeEnum.SIMPLE
  );
  const [ queryPromptName, setQueryPromptName ] = useState( qPromptName || '' );
  const [ queryTag, setQueryTag ] = useState( qTag || '' );
  const [ showCreate, setShowCreate ] = useState<boolean>( false );

  const handlePromptCreate = useCallback(
    ( e: FormEvent<HTMLFormElement> ) => {
      e.preventDefault();

      const formData = new FormData( e.currentTarget );
      const name = formData.get( 'name' ) as string;
      const description = formData.get( 'description' ) as string;

      navigate(
        dedent`/prompts/create
      ?c=${ true.toString() }
      &d=${ encodeURIComponent( description ) }
      &n=${ name }
      &ty=${ newPromptType }`
      );
    },
    [ newPromptType, navigate ]
  );

  const handleModalClose = useCallback( () => {
    setShowCreate( false );
  }, [] );

  const handlePromptTypeChange = useCallback( ( type: PromptTypeEnum ) => {
    setNewPromptType( type );
  }, [] );

  const handleSearch = useCallback( () => {
    const params = new URLSearchParams();
    if ( queryPromptName ) params.set( 'n', queryPromptName );
    if ( queryTag ) params.set( 'ta', queryTag );
    navigate( pathname + '?' + params.toString() );
  }, [ queryPromptName, queryTag, navigate, pathname ] );

  const handleSearchKeyDown = useCallback(
    ( e: React.KeyboardEvent<HTMLInputElement> ) => {
      if ( e.key === 'Enter' ) {
        handleSearch();
      }
    },
    [ handleSearch ]
  );

  const handleToggleCreate = useCallback( () => {
    setShowCreate( ( prev ) => !prev );
  }, [] );

  const handleQueryChange = useCallback(
    ( e: React.ChangeEvent<HTMLInputElement> ) => {
      setQueryPromptName( e.target.value );
    },
    []
  );

  const handleTagChange = useCallback(
    ( e: React.ChangeEvent<HTMLInputElement> ) => {
      setQueryTag( e.target.value );
    },
    []
  );

  return (
    <div className="page">
      <ToastContainer />
      <div className={ clsx( 'layout__row', 'pad', 'layout__info' ) }>
        <span className="info_name">
          <Heading as="h2">Saved Prompts</Heading>
        </span>

        <div className="layout__row">
          <Button
            variant="primary"
            icon={ <Icon icon={ FiPlus } /> }
            onClick={ handleToggleCreate }
          >
            Create Prompt
          </Button>
        </div>
      </div>

      <>
        {showCreate && (
          <ModalCreate
            newPromptType={ newPromptType }
            onPromptTypeChange={ handlePromptTypeChange }
            onPromptCreate={ handlePromptCreate }
            onClose={ handleModalClose }
          />
        )}

        <div className={ clsx( promptStyles.search_row ) }>
          <Input
            type="text"
            value={ queryPromptName }
            onChange={ handleQueryChange }
            onKeyDown={ handleSearchKeyDown }
            label="Filter by Prompt name"
            placeholder="Type a Prompt name"
            data-testid="prompts-search-input"
          />
          <Input
            type="text"
            value={ queryTag }
            onChange={ handleTagChange }
            onKeyDown={ handleSearchKeyDown }
            label="Filter by Tag name"
            placeholder="Filter by Tag name..."
            data-testid="prompts-tag-input"
          />
          <Button variant="secondary" onClick={ handleSearch }>
            Search
          </Button>
        </div>
        <p className={ promptStyles.description }>
          <strong>Saved Prompts</strong> are retrieved at runtime by{' '}
          <strong>Full-Code Agents</strong> via the{' '}
          <strong>Prompt Management API</strong>, enabling prompt updates
          without redeploying agent code.{' '}
          <a
            href={ DOCS_BASE_URL + '/agent_features/saved_prompts' }
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more here
          </a>
        </p>
        <div className={ clsx( 'layout__col', 'pad' ) }>
          <PromptsTable
            name={ qPromptName ?? '' }
            tags={ qTag ?? null }
            page={ page ? parseInt( page ) : 0 }
            onPageChange={ handlePageChange }
          />
        </div>
      </>
    </div>
  );
}

export default function Page() {
  return (
    <Suspense fallback={ <Loader /> }>
      <PromptsPage />
    </Suspense>
  );
}
